import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Chip,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  DialogContentText
} from '@mui/material';
import { Add, Delete, Warning, Edit } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import questionnairesService from '../../../services/questionnairesService';
import QuestionariesForm from './QuestionariesForm';
import EditQuestionnaireModal from './EditQuestionnaireModal';

const Questionaries = () => {
  const [questionnaires, setQuestionnaires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingData, setEditingData] = useState(null);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({
    open: false,
    questionnaireId: null,
    questionnaireTitle: ''
  });
  const [isDeleting, setIsDeleting] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedQuestionnaireId, setSelectedQuestionnaireId] = useState(null);

  useEffect(() => {
    fetchQuestionnaires();
  }, []);

  const fetchQuestionnaires = async () => {
    try {
      setLoading(true);
      const data = await questionnairesService.getQuestionnaires();
      const processedData = data.map(item => ({
        ...item,
        id: item.id || Math.random().toString(36).substr(2, 9),
      }));
      setQuestionnaires(processedData);
    } catch (err) {
      setError('Error al cargar los cuestionarios');
      console.error('Error fetching questionnaires:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = () => {
    setEditingData(null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingData(null);
  };

  const handleEdit = (row) => {
    setSelectedQuestionnaireId(row.id);
    setEditModalOpen(true);
  };

  const handleDeleteClick = (row) => {
    setDeleteConfirmDialog({
      open: true,
      questionnaireId: row.id,
      questionnaireTitle: row.title
    });
  };

  const handleCloseDeleteDialog = () => {
    setDeleteConfirmDialog({
      open: false,
      questionnaireId: null,
      questionnaireTitle: ''
    });
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirmDialog.questionnaireId) return;
    try {
      setIsDeleting(true);
      await questionnairesService.deleteQuestionnaire(deleteConfirmDialog.questionnaireId);
      await fetchQuestionnaires();
      handleCloseDeleteDialog();
    } catch (err) {
      console.error('Error eliminando cuestionario:', err);
      setError('Error al eliminar el cuestionario');
    } finally {
      setIsDeleting(false);
    }
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'title', headerName: 'Título', flex: 1 },
    { field: 'description', headerName: 'Descripción', flex: 1.5 },
    {
      field: 'uploadedByName',
      headerName: 'Subido por',
      width: 150,
      valueGetter: (_, row) => row?.uploadedBy?.name || 'N/A',
    },
    {
      field: 'uploadedByCedula',
      headerName: 'Cédula',
      width: 120,
      valueGetter: (_, row) => row?.uploadedBy?.cedula || 'N/A',
    },
    {
      field: 'label',
      headerName: 'Etiqueta',
      width: 140,
      renderCell: (params) => {
        const label = params?.row?.uploadedBy?.label;
        return label ? (
          <Chip
            label={label.name}
            size="small"
            color={label.isProfile ? "success" : "default"}
          />
        ) : 'N/A';
      }
    },
    {
      field: 'questionsCount',
      headerName: 'Preguntas',
      width: 120,
      valueGetter: (_, row) => row?.questions?.length || 0,
    },
    {
      field: 'createdAt',
      headerName: 'Creado',
      width: 160,
      valueGetter: (_, row) => row?.createdAt ? new Date(row.createdAt) : null,
    },
    {
      field: 'updatedAt',
      headerName: 'Actualizado',
      width: 160,
      valueGetter: (_, row) => row?.updatedAt ? new Date(row.updatedAt) : null,
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 280,
      sortable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={2}>
          <Button
            size="small"
            variant="contained"
            startIcon={<Edit />}
            onClick={() => handleEdit(params.row)}
            className="background-green-aqua hover:bg-green-600 shadow-md hover:shadow-lg transition-all duration-200 font-semibold text-white"
            sx={{
              minWidth: '80px',
              height: '32px',
              borderRadius: '8px',
              textTransform: 'none',
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(77, 171, 164, 0.3)'
              }
            }}
          >
            Editar
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<Delete />}
            onClick={() => handleDeleteClick(params.row)}
            className="bg-red-600 hover:bg-red-700 text-white transition-all duration-200 font-semibold"
            sx={{
              minWidth: '80px',
              height: '32px',
              borderRadius: '8px',
              textTransform: 'none',
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(239, 68, 68, 0.4)'
              }
            }}
          >
            Eliminar
          </Button>
        </Stack>
      )
    },
  ];

  if (loading) {
    return (
      <Box className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <CircularProgress />
        <Typography color="text.secondary">Cargando cuestionarios...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="flex items-center justify-center min-h-[400px]">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box className="p-6 bg-gradient-to-br from-white to-gray-50 rounded-xl shadow-lg space-y-6">
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Box className="flex items-center gap-3">
          <Box className="w-8 h-8 bg-green-aqua rounded-lg flex items-center justify-center">
            <Typography className="text-white font-bold text-lg">?</Typography>
          </Box>
          <Typography variant="h5" className="font-bold text-green-aqua">
            Gestión de Cuestionarios
          </Typography>
        </Box>
        <Button
          onClick={handleOpenDialog}
          className="background-green-aqua hover:bg-green-600 hover:shadow-lg transition-all duration-200 font-semibold text-white"
          startIcon={<Add />}
          sx={{
            borderRadius: '10px',
            textTransform: 'none',
            padding: '10px 20px',
            height: '44px',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 8px 20px rgba(77, 171, 164, 0.4)'
            }
          }}
        >
          Nuevo Cuestionario
        </Button>
      </Stack>

      <Box className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="h-[600px]">
          <DataGrid
            rows={questionnaires}
            columns={columns}
            pageSizeOptions={[5, 10, 20]}
            initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
            disableRowSelectionOnClick
            getRowId={(row) => row.id}
            sx={{
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#4DABA4',
                color: 'black',
                fontWeight: 'bold',
                fontSize: '14px',
                borderBottom: '3px solid #3a8a84',
                '& .MuiDataGrid-columnHeaderTitle': {
                  color: 'black',
                  fontWeight: 'bold',
                  fontSize: '14px',
                },
              },
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid #e5e7eb',
                fontSize: '13px',
                padding: '12px 16px',
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: '#f0f9ff',
                transition: 'background-color 0.2s ease',
              },
              '& .MuiDataGrid-footerContainer': {
                borderTop: '2px solid #4DABA4',
                backgroundColor: '#f8fafc',
              },
              '& .MuiDataGrid-virtualScroller': {
                backgroundColor: '#ffffff',
              },
              '& .MuiDataGrid-main': {
                backgroundColor: '#ffffff',
              },
            }}
          />
        </div>
      </Box>

      {/* Diálogo para crear/editar cuestionario */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle className="background-green-aqua flex items-center gap-2">
          <Add />
          {editingData ? 'Editar Cuestionario' : 'Crear Nuevo Cuestionario'}
        </DialogTitle>
        <DialogContent dividers className="bg-gray-50">
          <QuestionariesForm initialData={editingData} />
        </DialogContent>
        <DialogActions className="bg-gray-50 p-4">
          <Button 
            onClick={handleCloseDialog} 
            className="text-gray-600 hover:text-gray-800 border-gray-300 hover:bg-gray-100 transition-all"
            variant="outlined"
          >
            Cancelar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de edición de cuestionario */}
      <EditQuestionnaireModal
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedQuestionnaireId(null);
        }}
        questionnaireId={selectedQuestionnaireId}
      />

      {/* Diálogo de confirmación de eliminación */}
      <Dialog
        open={deleteConfirmDialog.open}
        onClose={handleCloseDeleteDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle className="flex items-center gap-2 text-red-600 bg-red-50 border-b border-red-200">
          <Warning className="text-red-500" />
          <Typography variant="h6" className="font-bold">
            Confirmar eliminación
          </Typography>
        </DialogTitle>
        <DialogContent className="bg-white py-6">
          <DialogContentText className="text-gray-700 mb-4">
            ¿Estás seguro de que deseas eliminar el cuestionario{' '}
            <strong className="text-red-600">"{deleteConfirmDialog.questionnaireTitle}"</strong>?
          </DialogContentText>
          <DialogContentText className="text-gray-500 bg-yellow-50 p-3 rounded-lg border border-yellow-200">
            ⚠️ Esta acción no se puede deshacer. Se eliminará el cuestionario y todas sus preguntas asociadas.
          </DialogContentText>
        </DialogContent>
        <DialogActions className="p-4 gap-3 bg-gray-50">
          <Button
            onClick={handleCloseDeleteDialog}
            variant="outlined"
            className="text-gray-700 border-gray-300 hover:bg-gray-100 transition-all duration-200 font-semibold"
            disabled={isDeleting}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              borderWidth: '2px',
              minWidth: '100px',
              height: '40px',
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(107, 114, 128, 0.2)'
              }
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmDelete}
            className="bg-red-600 hover:bg-red-700 text-white transition-all duration-200 font-semibold shadow-md hover:shadow-lg"
            startIcon={isDeleting ? <CircularProgress size={16} color="inherit" /> : <Delete />}
            disabled={isDeleting}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              minWidth: '120px',
              height: '40px',
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: '0 6px 16px rgba(239, 68, 68, 0.4)'
              }
            }}
          >
            {isDeleting ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Questionaries;
